/**
 * W1 — Plan workspace / AFE hub. The event-scoped home of a plan: it shows the
 * business identity (UWI · EventCode · AFE, Z-spec §21c), the plan status +
 * version, a Publish action (→ W9), and the design datasets as **adaptive tabs**
 * driven by the event's letter + is_conventional (the seed SECTION_MATRIX). Tabs
 * that own a sibling authoring screen (W2–W6) LINK there; objetivo / atributos
 * render inline here (no dedicated screen). Built to w1-plan-workspace.html.
 *
 * Context comes from the wellContext slice (W0 sets well/event/plan). Direct
 * navigation with no active event falls back to the selected well's first event,
 * else an empty state that points back to the finder.
 */
import { useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "@app/hooks";
import { setActiveEvent, setActivePlan } from "@app/slices/wellContextSlice";
import { useLocale } from "@hooks/useLocale";
import { fmtMoney, fmtNum } from "@lib/format";
import { asArray } from "@lib/asArray";
import { useGetWellsQuery } from "@api/mApi";
import {
  useGetWellEventsQuery,
  useGetEventPlansQuery,
  useGetPlanDatasetsQuery,
} from "@api/pApi";
import type { EventOut, AttributeRow } from "@api/types";
import type { WellView } from "@api/mappers";
import { QueryState } from "@components/ui/QueryState";
import {
  enrichEventCode,
  sectionsFor,
  EVENT_LETTER_OP_VAR,
  type EventEnrichment,
  type SectionKey,
} from "@lib/eventEnrichment";
import { latestPlan, STATUS_KEY, statusClass } from "@features/planFinder/planCatalog";
import { datasetTabs, planKpis, planCurve } from "./workspaceModel";
import { PlanCurve } from "./PlanCurve";
import "./workspace.css";

export function Workspace() {
  const { t } = useLocale();
  const navigate = useNavigate();

  const selectedWellId = useAppSelector((s) => s.wellContext.selectedWellId);
  const activeEventId = useAppSelector((s) => s.wellContext.activeEventId);

  const wellsQ = useGetWellsQuery();
  // A real backend can return a non-array body ({} / error envelope) where an
  // array is expected — coerce before .find so it never throws (Oxbo-Z crash class).
  const well = useMemo(() => {
    const wells = asArray<WellView>(wellsQ.data);
    return wells.find((w) => w.id === selectedWellId) ?? wells[0] ?? null;
  }, [wellsQ.data, selectedWellId]);

  const eventsQ = useGetWellEventsQuery(well?.uwi ?? "", { skip: !well });
  const events = useMemo(() => asArray<EventOut>(eventsQ.data), [eventsQ.data]);

  // Resolve the active event: slice value if it belongs to this well, else the
  // well's first event (so the hub is never blank when a well is selected).
  const activeEvent = useMemo<EventOut | null>(() => {
    if (events.length === 0) return null;
    return events.find((e) => e.id === activeEventId) ?? events[0] ?? null;
  }, [events, activeEventId]);

  return (
    <div>
      <div className="screen-head">
        <h1>{t("ws.title")}</h1>
        <span className="badge info">{t("ws.badge")}</span>
        <div className="actions">
          <button
            type="button"
            className="btn primary"
            disabled={!activeEvent}
            onClick={() => navigate("/publish")}
          >
            <span className="ic ic-export" aria-hidden="true" /> {t("ws.publish")}
          </button>
        </div>
      </div>

      <QueryState
        isLoading={wellsQ.isLoading || (!!well && eventsQ.isLoading)}
        isError={wellsQ.isError || eventsQ.isError}
        isEmpty={!well || events.length === 0}
        emptyMessage={t("ws.empty")}
        onRetry={() => void eventsQ.refetch()}
      >
        {well && activeEvent && (
          <WorkspaceBody wellName={well.commonName} events={events} activeEvent={activeEvent} />
        )}
      </QueryState>
    </div>
  );
}

/** The hub body once a well + event are resolved (owns the plans + datasets). */
function WorkspaceBody({
  wellName,
  events,
  activeEvent,
}: {
  wellName: string;
  events: EventOut[];
  activeEvent: EventOut;
}) {
  const { t, locale } = useLocale();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  const plansQ = useGetEventPlansQuery(activeEvent.id);
  const plan = useMemo(() => latestPlan(plansQ.data), [plansQ.data]);
  const versionCount = asArray(plansQ.data).length;
  const datasetsQ = useGetPlanDatasetsQuery(plan?.id ?? "", { skip: !plan });
  const datasets = datasetsQ.data;

  const en = enrichEventCode(activeEvent.event_code);
  const conv = en.isConventional ?? activeEvent.is_conventional;
  const opVar = en.letter ? EVENT_LETTER_OP_VAR[en.letter] : "--surface-3";

  const sections = useMemo(() => sectionsFor(en.letter, conv), [en.letter, conv]);
  const tabs = useMemo(() => datasetTabs(sections), [sections]);

  // The inline hub panel selected by the tabs (objetivo / atributos). Held in
  // React state so a re-render (data arriving) never resets the selection —
  // the DOM-mutation approach it replaces was reset by every render.
  const [activePanel, setActivePanel] = useState<SectionKey>("objetivo");

  const kpis = useMemo(
    () =>
      planKpis(
        datasets,
        plan?.authorized_total ?? null,
        (n) => fmtNum(n, locale),
        (n) => fmtMoney(n, locale, { compact: true }),
      ),
    [datasets, plan?.authorized_total, locale],
  );
  const curve = useMemo(() => planCurve(datasets?.programa), [datasets?.programa]);

  function pickEvent(ev: EventOut) {
    dispatch(setActiveEvent(ev.id));
    dispatch(setActivePlan(null));
  }

  const afe = plan?.afe_no ?? activeEvent.afe_no;

  return (
    <>
      {/* event / plan context selector */}
      <EventSwitcher
        wellName={wellName}
        events={events}
        activeEvent={activeEvent}
        onPick={pickEvent}
      />

      {/* context chips: well · event · afe · status · version */}
      <div className="chip-ctx ws-ctx">
        <span className="k">{t("ws.ctx.well")}</span>
        <span className="mono">{wellName}</span>
        <span className="dim">·</span>
        <span className="k">{t("ws.ctx.event")}</span>
        <span className="letter-badge" style={{ background: `var(${opVar})` }}>
          <span className="sw" />
          {en.letter ?? "?"} · {activeEvent.event_code} — {en.name}
        </span>
        <span className="dim">·</span>
        <span className="k">{t("ws.ctx.afe")}</span>
        <span className="mono">{afe ?? t("common.na")}</span>
        {plan ? (
          <>
            <span className="dim">·</span>
            <span className={`status-pill ${statusClass(plan.status)}`}>
              ● {t(STATUS_KEY[plan.status], { n: plan.version })}
            </span>
            <span className="verchip">v{plan.version}</span>
          </>
        ) : (
          <>
            <span className="dim">·</span>
            <span className="status-pill draft">{t("plan.status.none")}</span>
          </>
        )}
        {conv === false && (
          <span className="badge crit" title={t("conv.unconvention")}>
            {t("conv.unconvention")}
          </span>
        )}
      </div>

      <div className="section-note">
        <span className="qm">i</span>
        <span>{t("ws.note")}</span>
      </div>

      {/* KPI chips */}
      <div className="kpis">
        {kpis.map((k) => (
          <div className="kpi" key={k.labelKey}>
            <div className="v tab-nums">{k.value}</div>
            <div className="l">{t(k.labelKey)}</div>
          </div>
        ))}
      </div>

      {/* adaptive dataset tabs */}
      <section className="card" style={{ padding: 16, marginTop: 16 }}>
        <div className="tabs ws-tabs" role="tablist" aria-label={t("ws.tabs.aria")}>
          {tabs.map((tab) =>
            tab.route ? (
              <Link
                key={tab.section}
                to={tab.route}
                className={tab.section === "fractura" ? "frac" : undefined}
              >
                {t(tab.labelKey)}
                <span className="ic ic-export go" aria-hidden="true" />
              </Link>
            ) : (
              <button
                key={tab.section}
                type="button"
                className={`ws-tab${tab.section === activePanel ? " active" : ""}`}
                aria-selected={tab.section === activePanel}
                onClick={() => setActivePanel(tab.section)}
              >
                {t(tab.labelKey)}
              </button>
            ),
          )}
        </div>

        <QueryState
          isLoading={!!plan && datasetsQ.isLoading}
          isError={datasetsQ.isError}
          onRetry={() => void datasetsQ.refetch()}
        >
          {/* inline hub panels — objetivo (default) + atributos, toggled by state */}
          {sections.includes("objetivo") && activePanel === "objetivo" && (
            <div className="tab-panel active" role="tabpanel">
              <h2 style={{ margin: "0 0 10px" }}>{t("ws.objetivo.title")}</h2>
              <AttributeGrid
                rows={asArray<AttributeRow>(datasets?.objetivo)}
                emptyLabel={t("ws.objetivo.empty")}
              />
            </div>
          )}
          {sections.includes("atributos") && activePanel === "atributos" && (
            <div className="tab-panel active" role="tabpanel">
              <h2 style={{ margin: "0 0 10px" }}>{t("ws.atributos.title")}</h2>
              <AttributeGrid
                rows={asArray<AttributeRow>(datasets?.atributos)}
                emptyLabel={t("ws.atributos.empty")}
              />
            </div>
          )}
        </QueryState>

        {/* the plan curve, folded from the program (a preview of W5's output) */}
        {curve.length > 0 && <PlanCurve points={curve} opVar={opVar} />}
      </section>

      {/* versions / publish footer strip */}
      <div className="ws-footer">
        <div className="card">
          <h2>{t("ws.version.title")}</h2>
          {plan ? (
            <>
              <div className="ver-line">
                <span className="verchip" style={{ fontSize: 13, padding: "5px 12px" }}>
                  v{plan.version} · {t(STATUS_KEY[plan.status], { n: plan.version })}
                </span>
                {plan.actor && (
                  <span className="dim" style={{ fontSize: 12.5 }}>
                    {t("ws.version.editedBy", { actor: plan.actor })}
                  </span>
                )}
              </div>
              <div className="dim" style={{ fontSize: 12.5 }}>
                {t("ws.version.count", { n: versionCount || 1 })}
              </div>
            </>
          ) : (
            <div className="dim" style={{ fontSize: 12.5 }}>
              {t("ws.version.none")}
            </div>
          )}
          <div style={{ marginTop: 12, display: "flex", gap: 8 }}>
            <Link to="/versions" className="btn sm">
              {t("ws.version.history")}
            </Link>
          </div>
        </div>
        <div className="card">
          <h2>{t("ws.publishCard.title")}</h2>
          <div className="dim" style={{ fontSize: 12.5, margin: "0 0 12px", lineHeight: 1.6 }}>
            {t("ws.publishCard.body")}
          </div>
          <button
            type="button"
            className="btn primary"
            style={{ width: "100%", justifyContent: "center" }}
            disabled={!plan}
            onClick={() => navigate("/publish")}
          >
            <span className="ic ic-export" aria-hidden="true" /> {t("ws.publishCard.action")}
          </button>
        </div>
      </div>
    </>
  );
}

/** The event / plan switcher popover (mirrors W0's evtchip, as a menu). */
function EventSwitcher({
  wellName,
  events,
  activeEvent,
  onPick,
}: {
  wellName: string;
  events: EventOut[];
  activeEvent: EventOut;
  onPick: (ev: EventOut) => void;
}) {
  const { t } = useLocale();
  const en = enrichEventCode(activeEvent.event_code);
  const opVar = en.letter ? EVENT_LETTER_OP_VAR[en.letter] : "--surface-3";

  return (
    <details className="evtsw-wrap">
      <summary className="evtsw" role="button">
        <span className="av" style={{ background: `var(${opVar})` }}>
          {en.letter ?? "?"}
        </span>
        <span className="nm">
          <b>
            {activeEvent.event_code} — {en.name}
          </b>
          <small>
            {wellName} · {activeEvent.afe_no ?? t("common.na")}
          </small>
        </span>
        <span className="chev ic ic-chevron" aria-hidden="true" />
      </summary>
      <div className="evtsw-pop" role="menu" aria-label={t("ws.switcher.aria")}>
        <h5>{t("ws.switcher.heading", { well: wellName })}</h5>
        {events.map((ev) => (
          <EventOption
            key={ev.id}
            event={ev}
            active={ev.id === activeEvent.id}
            onPick={() => onPick(ev)}
          />
        ))}
      </div>
    </details>
  );
}

/** One event row in the switcher — its own enrichment for letter + conv badge. */
function EventOption({
  event,
  active,
  onPick,
}: {
  event: EventOut;
  active: boolean;
  onPick: () => void;
}) {
  const { t } = useLocale();
  const en: EventEnrichment = enrichEventCode(event.event_code);
  const conv = en.isConventional ?? event.is_conventional;
  const opVar = en.letter ? EVENT_LETTER_OP_VAR[en.letter] : "--surface-3";

  return (
    <button
      type="button"
      className={`evt-opt${active ? " active" : ""}`}
      role="menuitem"
      onClick={onPick}
    >
      <span className="letter" style={{ background: `var(${opVar})` }}>
        {en.letter ?? "?"}
      </span>
      <span>
        <b>{event.event_code}</b> — {en.name}
        <div className="meta">{event.afe_no ?? t("common.na")}</div>
      </span>
      <span className={`badge ${conv === false ? "crit" : "closed"}`} style={{ fontSize: 10.5 }}>
        {conv === false ? t("conv.no") : t("conv.yes")}
      </span>
    </button>
  );
}

/** A read-only attribute grid (objetivo / atributos hub panels). */
function AttributeGrid({
  rows,
  emptyLabel,
}: {
  rows: { seq: number; name: string; value: string; unit: string | null }[];
  emptyLabel: string;
}) {
  if (rows.length === 0) {
    return (
      <div className="empty-state" style={{ padding: "28px 20px", color: "var(--dim)" }}>
        {emptyLabel}
      </div>
    );
  }
  return (
    <div className="attrgrid">
      {rows.map((r) => (
        <div className="attr" key={r.seq}>
          <div className="k">{r.name}</div>
          <div className="v">
            {r.value}
            {r.unit && <small>{r.unit}</small>}
          </div>
        </div>
      ))}
    </div>
  );
}
